import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import {
  TEST_MODULE_PARAMETER_TYPES,
  TestModuleParameter,
  TestModuleParameterType,
} from '../../../../models/test-module.model';

/** Bezeichner-Regel für Automatisierung — identisch zum BE-Guard (NEX-704). */
const IDENTIFIER = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

/**
 * Card „Parameter / Schnittstelle" des Modul-Formulars (NEX-704). Rein gesteuert:
 * liest `parameters`/`returns` als Signale und meldet jede Mutation als neue Liste
 * nach oben (Daten runter, Events hoch) — analog dem changed()-Muster der
 * TestStepTableComponent. Persistenz erfolgt beim regulären Modul-Save.
 */
@Component({
  selector: 'app-test-module-parameters-card',
  imports: [TranslatePipe, MatIcon],
  templateUrl: './test-module-parameters-card.component.html',
  styleUrl: './test-module-parameters-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestModuleParametersCardComponent {
  readonly parameters = input<TestModuleParameter[]>([]);
  readonly returns = input<TestModuleParameter[]>([]);
  readonly readOnly = input(false);

  readonly parametersChange = output<TestModuleParameter[]>();
  readonly returnsChange = output<TestModuleParameter[]>();
  /** Dirty-Signal für das Modul-Formular (analog TestStepTableComponent). */
  readonly changed = output<void>();
  /** true, sobald ein Name leer, kein gültiger Bezeichner oder ein Duplikat ist. */
  readonly invalidChange = output<boolean>();

  readonly parameterTypes = TEST_MODULE_PARAMETER_TYPES;
  // Rückgaben sind Ausgaben — SECRET ist dort weder sinnvoll noch erlaubt (BE-CHECK).
  readonly returnTypes = TEST_MODULE_PARAMETER_TYPES.filter((t) => t !== 'SECRET');

  readonly duplicateParameterNames = computed(() => this.duplicates(this.parameters()));
  readonly duplicateReturnNames = computed(() => this.duplicates(this.returns()));
  readonly hasInvalidParameters = computed(() => this.hasInvalid(this.parameters()));
  readonly hasInvalidReturns = computed(() => this.hasInvalid(this.returns()));

  // Eigener Client-ID-Präfix je Draft: das BE führt EINE clientId→UUID-Map für Steps,
  // Embeds UND Parameter — ein mit den Step-IDs (`local-…`) geteilter Namensraum würde
  // beim Remap kollidieren.
  private clientIdSeq = 0;

  trackRow(_index: number, row: TestModuleParameter): string {
    return row.clientId ?? row.id ?? String(_index);
  }

  isNameInvalid(name: string, duplicates: Set<string>): boolean {
    const trimmed = name.trim();
    return trimmed.length === 0 || !IDENTIFIER.test(trimmed) || duplicates.has(trimmed);
  }

  // ── Eingabe-Parameter ─────────────────────────────────────────────────
  addParameter(): void {
    this.emitParameters([
      ...this.parameters(),
      { clientId: `param-${this.clientIdSeq++}`, name: '', type: 'TEXT', required: false, defaultValue: null },
    ]);
  }

  renameParameter(index: number, name: string): void {
    this.emitParameters(this.patch(this.parameters(), index, { name }));
  }

  retypeParameter(index: number, type: TestModuleParameterType): void {
    // SECRET-Parameter dürfen keinen Default speichern (BE-Guard) — beim Umschalten
    // auf SECRET den Default verwerfen, statt den User in einen 400 laufen zu lassen.
    const change = type === 'SECRET' ? { type, defaultValue: null } : { type };
    this.emitParameters(this.patch(this.parameters(), index, change));
  }

  toggleParameterRequired(index: number): void {
    const current = this.parameters()[index];
    this.emitParameters(this.patch(this.parameters(), index, { required: !current.required }));
  }

  setParameterDefault(index: number, defaultValue: string): void {
    this.emitParameters(this.patch(this.parameters(), index, { defaultValue: defaultValue || null }));
  }

  removeParameter(index: number): void {
    this.emitParameters(this.parameters().filter((_, i) => i !== index));
  }

  // ── Rückgaben ─────────────────────────────────────────────────────────
  addReturn(): void {
    this.emitReturns([
      ...this.returns(),
      {
        clientId: `return-${this.clientIdSeq++}`,
        name: 'neuerWert',
        type: 'TEXT',
        required: false,
        defaultValue: null,
      },
    ]);
  }

  renameReturn(index: number, name: string): void {
    this.emitReturns(this.patch(this.returns(), index, { name }));
  }

  retypeReturn(index: number, type: TestModuleParameterType): void {
    this.emitReturns(this.patch(this.returns(), index, { type }));
  }

  removeReturn(index: number): void {
    this.emitReturns(this.returns().filter((_, i) => i !== index));
  }

  // ── intern ────────────────────────────────────────────────────────────
  private patch(
    list: TestModuleParameter[],
    index: number,
    change: Partial<TestModuleParameter>,
  ): TestModuleParameter[] {
    return list.map((row, i) => (i === index ? { ...row, ...change } : row));
  }

  private emitParameters(next: TestModuleParameter[]): void {
    this.parametersChange.emit(next);
    this.afterMutation(next, this.returns());
  }

  private emitReturns(next: TestModuleParameter[]): void {
    this.returnsChange.emit(next);
    this.afterMutation(this.parameters(), next);
  }

  private afterMutation(parameters: TestModuleParameter[], returns: TestModuleParameter[]): void {
    this.changed.emit();
    this.invalidChange.emit(this.hasInvalid(parameters) || this.hasInvalid(returns));
  }

  private hasInvalid(list: TestModuleParameter[]): boolean {
    const duplicates = this.duplicates(list);
    return list.some((row) => this.isNameInvalid(row.name, duplicates));
  }

  private duplicates(list: TestModuleParameter[]): Set<string> {
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    for (const row of list) {
      const name = row.name.trim();
      if (!name) continue;
      if (seen.has(name)) {
        duplicates.add(name);
      }
      seen.add(name);
    }
    return duplicates;
  }
}
